// "Add digital employee" (DE-WIZARD, review #12): the wizard's pure parts, shared by the create
// flow (add-de-wizard.tsx) and the profile's edit mode (edit-de.tsx). The template choices (the
// self-serve catalog — the empty agent is "Blank" — and the done-for-you templates the operator sets up),
// the steps, the form, the KPI defaults a template brings (contracts `deTemplateMetrics` over
// the company's metric definitions; AGENT-RECORD: every template brings the base set) and the
// model rows.
// Zod-free entry: `@companyos/schema/contracts` would load zod, whose `new Function` probe is a
// CSP violation in the browser (no `unsafe-eval`).
import { deTemplateMetrics } from "@companyos/schema/de-metric-catalog";
import type { OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../../api/client";
import type { MetricDef } from "../../../api/types";
import type { CompanyAccess } from "../../company";
import type { AccessFamily, AccessLevel, RoleCard, RolePlan } from "../../templates/role-model";

export type SelfServeTemplate = OperationOutput<"agents.selfServeTemplates">["data"][number];
export type DeJobView = OperationOutput<"digitalEmployees.job">;
export type Duty = "daily" | "weekly" | "monthly";
export type Path = "self_serve" | "done_for_you";

export const selfServeTemplatesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: ["self-serve-templates", co] as const,
    queryFn: () => api.client.agents.selfServeTemplates({ params: { co } }),
    staleTime: 60_000,
  });

export const jobKey = (co: string, de: string) => ["company", co, "de", de, "job"] as const;
export const jobQuery = (api: Api, co: string, de: string) =>
  queryOptions({
    queryKey: jobKey(co, de),
    queryFn: () => api.client.digitalEmployees.job({ params: { co, id: de } }),
    retry: false,
  });

/** 05: a self-serve deploy is staged by the company's owner or admin (a person). */
export function mayStage(company: CompanyAccess): boolean {
  return company.roles.includes("owner") || company.roles.includes("admin");
}

/** One way to start: a self-serve template (deployed by the owner or an admin) or the operator's. */
export type TemplateChoice = {
  id: string;
  name: string;
  hint: string;
  path: Path;
  /** `digital_employee.template_key`: the self-serve template's key, or the operator's. */
  templateKey: string;
  /** The self-serve version deployed (null: done-for-you). */
  version: SelfServeTemplate | null;
  blank: boolean;
  unverified: boolean;
  /** WF-POLISH: Custom — defined right here (role, goal, backstory on the Name & seat step). */
  custom?: boolean;
  /**
   * TEMPLATES-T2: a role template (`roleTemplates.list`), done for you: the wizard is prefilled
   * from its plan and finishing applies the role (`digitalEmployees.applyRole`).
   */
  roleTemplateId?: string;
};

/** TEMPLATES-T2: the role templates as choices (done for you, the role's key). */
export function roleChoicesOf(roles: RoleCard[]): TemplateChoice[] {
  return roles.map((r) => ({
    id: `role:${r.template.id}`,
    name: r.template.name,
    hint: r.summary,
    path: "done_for_you" as const,
    templateKey: r.template.key,
    version: null,
    blank: false,
    unverified: false,
    roleTemplateId: r.template.id,
  }));
}

/** The plan's KPIs as rows: names and targets from the plan, defined at apply (`key`). */
export function roleKpiRows(plan: RolePlan, metrics: MetricDef[]): KpiRow[] {
  return plan.kpis.map((k) => {
    const m = metrics.find((x) => x.key === k.key && !x.archived_at);
    return {
      metric_def_id: k.metric_def_id ?? m?.id ?? `role:${k.key}`,
      key: k.key,
      name: k.name,
      unit: m?.unit ?? "number",
      target: k.target === null ? "" : String(k.target),
      reports: false,
      duty: "weekly" as const,
    };
  });
}

/**
 * WF-POLISH (Andrei, 2026-10-27: "adding them is kind of taking from templates but there
 * should be also a custom option"): a DE defined in the wizard — name, role, goal, backstory,
 * reports-to (its seat's supervisor) and seat — with no template behind it. It goes the
 * done-for-you way (the operator stages any deploy), so no self-serve rule changes.
 */
export const CUSTOM_CHOICE: TemplateChoice = {
  id: "custom",
  name: "Custom",
  hint: "Write its role, goal and backstory yourself. No template.",
  path: "done_for_you",
  templateKey: "custom",
  version: null,
  blank: true,
  unverified: false,
  custom: true,
};

/** The operator's templates a company asks for (done-for-you; 13 names `recruiting_de`). */
const managedChoices = (operator: string): TemplateChoice[] => [
  {
    id: "managed:recruiting_de",
    name: "Recruiting DE",
    hint: `${operator} sets it up from its recruiting template. Comes with the recruiting KPIs and weekly targets.`,
    path: "done_for_you",
    templateKey: "recruiting_de",
    version: null,
    blank: false,
    unverified: false,
  },
  {
    id: "managed:blank",
    name: "Blank",
    hint: `${operator} sets it up from your standing orders. No KPIs to start with.`,
    path: "done_for_you",
    templateKey: "custom",
    version: null,
    blank: true,
    unverified: false,
  },
];

/** The choices: self-serve first when the caller may deploy (the empty agent is Blank). */
export function choicesOf(
  selfServe: SelfServeTemplate[] | null,
  roles: RoleCard[] = [],
  operator = "Operator",
): TemplateChoice[] {
  const own = (selfServe ?? []).map((t): TemplateChoice => {
    const empty = t.template.template_class === "empty";
    return {
      id: `self:${t.version.id}`,
      name: empty ? "Blank" : t.template.name,
      hint: empty
        ? "No skills: it follows the standing orders you write. Shown as unverified."
        : "A basic template with its skills and a verification skill.",
      path: "self_serve",
      templateKey: t.template.key,
      version: t,
      blank: empty,
      unverified: empty,
    };
  });
  return [CUSTOM_CHOICE, ...own, ...roleChoicesOf(roles), ...managedChoices(operator)];
}

export const NEW_SEAT = "__new";

export const STEPS = [
  { key: "template", label: "Template", hint: "Or blank" },
  { key: "seat", label: "Name & seat", hint: "Reports to, team" },
  { key: "job", label: "Job", hint: "Standing orders" },
  { key: "kpis", label: "KPIs", hint: "Targets, reporting" },
  { key: "models", label: "Models & budget", hint: "Spend" },
  { key: "channels", label: "Channels & access", hint: "Chat, approvals" },
  { key: "review", label: "Review", hint: "Deploy or submit" },
] as const;
export type StepKey = (typeof STEPS)[number]["key"];
export const STEP_KEYS = STEPS.map((s) => s.key) as StepKey[];

export type KpiRow = {
  /** A role KPI not defined yet holds `role:{key}` until the role is applied. */
  metric_def_id: string;
  /** TEMPLATES-T2: the role KPI's metric key (its definition comes from `applyRole`). */
  key?: string;
  name: string;
  unit: MetricDef["unit"];
  /** The weekly target as typed ("" = none). */
  target: string;
  reports: boolean;
  duty: Duty;
};

export type ModelRow = { key: number; role: string; model: string };
let nextRow = 0;
export const modelRow = (role: string, model: string): ModelRow => ({
  key: ++nextRow,
  role,
  model,
});
export const rowsOfMap = (map: Record<string, { model: string }> | null | undefined): ModelRow[] =>
  Object.entries(map ?? {})
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([role, e]) => modelRow(role, e.model));
/** The map as the override takes it (every model through the company's gateway). */
export const mapOfRows = (rows: ModelRow[]) =>
  Object.fromEntries(
    rows
      .filter((r) => r.role.trim() && r.model.trim())
      .map((r) => [r.role.trim(), { model: r.model.trim(), paths: ["gateway" as const] }]),
  );

/** The KPIs a template brings: its catalog entries the company defines, with default targets. */
export function templateKpis(templateKey: string, metrics: MetricDef[]): KpiRow[] {
  const byKey = new Map(metrics.filter((m) => !m.archived_at).map((m) => [m.key, m]));
  return deTemplateMetrics(templateKey).flatMap((e) => {
    const m = byKey.get(e.key);
    if (!m || e.tracking_mode === "context") return [];
    return [
      {
        metric_def_id: m.id,
        name: m.name,
        unit: m.unit,
        target: e.default_weekly_target === null ? "" : String(e.default_weekly_target),
        reports: false,
        duty: "weekly" as const,
      },
    ];
  });
}

export type Form = {
  choice: string;
  name: string;
  seat: string;
  seatTitle: string;
  roleId: string;
  supervisorSeatId: string;
  teamId: string;
  orders: string;
  kpis: KpiRow[];
  models: ModelRow[];
  amount: string;
  homeChannel: string;
  joins: string[];
  postApproval: boolean;
  approverSeatId: string;
  /** WF-POLISH: the Custom choice's profile. */
  role: string;
  goal: string;
  backstory: string;
  /** TEMPLATES-T2, a role's access step: levels set per family (absent: the plan's). */
  access: Partial<Record<AccessFamily, AccessLevel>>;
  confirmWiden: AccessFamily[];
  connectLater: AccessFamily[];
  verticalOverlayId: string;
  routineBoardId: string;
};

export const emptyForm = (): Form => ({
  access: {},
  confirmWiden: [],
  connectLater: [],
  verticalOverlayId: "",
  routineBoardId: "",
  role: "",
  goal: "",
  backstory: "",
  choice: "",
  name: "",
  seat: "",
  seatTitle: "",
  roleId: "",
  supervisorSeatId: "",
  teamId: "",
  orders: "",
  kpis: [],
  models: [],
  amount: "200",
  homeChannel: "",
  joins: [],
  postApproval: false,
  approverSeatId: "",
});

export const DUTY_LABELS: Record<Duty, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

export const currentMonth = () => new Date().toISOString().slice(0, 7);

/** A decimal target as the API takes it ("" = none; anything else not a number is invalid). */
export const targetValid = (t: string) => t.trim() === "" || /^\d{1,9}(\.\d{1,4})?$/.test(t.trim());
